import type { VaultCipher } from "@ciphernest/vault-core";

export type HealthIssueKind = "weak" | "reused" | "old" | "noTotp";

export type HealthIssue = {
  kind: HealthIssueKind;
  cipherId: string;
  name: string;
  detail: string;
};

export type HealthReport = {
  totalLogins: number;
  weak: HealthIssue[];
  reused: HealthIssue[];
  old: HealthIssue[];
  noTotp: HealthIssue[];
  score: number; // 0–100
};

const OLD_MS = 365 * 24 * 60 * 60 * 1000;

function isWeakPassword(password: string): string | null {
  if (!password) return "Empty password";
  if (password.length < 10) return `Only ${password.length} characters`;
  const classes =
    (/[a-z]/.test(password) ? 1 : 0) +
    (/[A-Z]/.test(password) ? 1 : 0) +
    (/\d/.test(password) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(password) ? 1 : 0);
  if (classes < 3) return "Low character variety";
  const common = [
    "password",
    "123456",
    "qwerty",
    "letmein",
    "welcome",
    "admin",
    "iloveyou",
  ];
  const lower = password.toLowerCase();
  if (common.some((c) => lower.includes(c))) return "Contains a common pattern";
  return null;
}

/** Client-side password health over decrypted vault logins. */
export function analyzePasswordHealth(ciphers: VaultCipher[]): HealthReport {
  const logins = ciphers.filter((c) => c.type === 1 && c.login && !c.deletedDate);
  const byPassword = new Map<string, VaultCipher[]>();

  for (const c of logins) {
    const pw = c.login?.password || "";
    if (!pw) continue;
    const list = byPassword.get(pw) || [];
    list.push(c);
    byPassword.set(pw, list);
  }

  const weak: HealthIssue[] = [];
  const reused: HealthIssue[] = [];
  const old: HealthIssue[] = [];
  const noTotp: HealthIssue[] = [];
  const now = Date.now();

  for (const c of logins) {
    const pw = c.login?.password || "";
    const weakReason = isWeakPassword(pw);
    if (weakReason) {
      weak.push({
        kind: "weak",
        cipherId: c.id,
        name: c.name,
        detail: weakReason,
      });
    }

    const peers = byPassword.get(pw);
    if (pw && peers && peers.length > 1) {
      reused.push({
        kind: "reused",
        cipherId: c.id,
        name: c.name,
        detail: `Same password on ${peers.length} logins`,
      });
    }

    const stamp = Date.parse(c.revisionDate || c.creationDate || "");
    if (stamp && now - stamp > OLD_MS) {
      const years = Math.floor((now - stamp) / (365 * 24 * 60 * 60 * 1000));
      old.push({
        kind: "old",
        cipherId: c.id,
        name: c.name,
        detail: years > 0 ? `Not updated in ${years}+ years` : "Older than 1 year",
      });
    }

    if (!c.login?.totp) {
      noTotp.push({
        kind: "noTotp",
        cipherId: c.id,
        name: c.name,
        detail: "No authenticator code stored",
      });
    }
  }

  const issueCount = weak.length + reused.length + old.length + noTotp.length;
  const maxIssues = Math.max(1, logins.length * 4);
  const score = Math.max(
    0,
    Math.min(100, Math.round(100 - (issueCount / maxIssues) * 100))
  );

  return {
    totalLogins: logins.length,
    weak,
    reused,
    old,
    noTotp,
    score,
  };
}
